'use client';

import {useEffect, useRef, useState} from 'react';
import {useLocale} from 'next-intl';
import {useReducedMotion} from '@/utils/useReducedMotion';
import {StyledCountUpValue} from './StatisticsSection.styles';

type CountUpProps = {
  end: number;
  duration?: number;
};

export default function CountUp({end, duration = 1500}: CountUpProps) {
  const locale = useLocale();
  const reducedMotion = useReducedMotion();
  const [value, setValue] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasStarted) {
          setHasStarted(true);
        }
      },
      {threshold: 0.3}
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [hasStarted]);

  useEffect(() => {
    if (!hasStarted || reducedMotion) return;

    const start = performance.now();
    let rafId: number;

    const isDecimal = end % 1 !== 0;
    const decimalPlaces = isDecimal ? (end.toString().split('.')[1]?.length ?? 1) : 0;

    const tick = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      // ease-out curve
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = eased * end;

      setValue(isDecimal ? parseFloat(current.toFixed(decimalPlaces)) : Math.round(current));

      if (progress < 1) {
        rafId = requestAnimationFrame(tick);
      }
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [hasStarted, end, duration, reducedMotion]);

  const isDecimal = end % 1 !== 0;
  const decimalPlaces = isDecimal ? (end.toString().split('.')[1]?.length ?? 1) : 0;
  // When the user prefers reduced motion, skip the tween and show the final value once visible.
  const displayedValue = reducedMotion && hasStarted ? end : value;
  const formatted = displayedValue.toLocaleString(locale, {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  });

  return <StyledCountUpValue ref={ref}>{formatted}</StyledCountUpValue>;
}
